import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isPasswordPwned } from '../../lib/passwordSecurity';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ChangePasswordMedecinPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [medecinId, setMedecinId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          navigate('/login', { replace: true });
          return;
        }

        // Vérifier que l'utilisateur est un médecin devant changer son mot de passe
        const { data: med, error: medErr } = await supabase
          .from('medecins')
          .select('id, must_change_password')
          .eq('user_id', user.id)
          .maybeSingle();

        if (medErr || !med) {
          // Pas un médecin → renvoyer vers l'accueil
          navigate('/', { replace: true });
          return;
        }

        if (!med.must_change_password) {
          // Le changement n'est plus requis → dashboard médecin
          navigate('/medecin/rendez-vous', { replace: true });
          return;
        }

        setMedecinId(med.id);
      } catch (err) {
        console.error('Erreur vérification statut médecin:', err);
        navigate('/', { replace: true });
      } finally {
        setChecking(false);
      }
    };

    checkStatus();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    // Vérification anti-fuite : refuser les mots de passe compromis (HaveIBeenPwned)
    const pwned = await isPasswordPwned(newPassword);
    if (pwned) {
      setError('Ce mot de passe a été compromis lors d\'une fuite de données. Pour votre sécurité, choisissez-en un autre.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      // Marquer le changement de mot de passe comme effectué
      if (medecinId) {
        await supabase
          .from('medecins')
          .update({ must_change_password: false, updated_at: new Date().toISOString() })
          .eq('id', medecinId);
      }

      setSuccess(true);

      setTimeout(() => {
        navigate('/medecin/rendez-vous', { replace: true });
      }, 2500);
    } catch (err: any) {
      console.error('Erreur mise à jour mot de passe:', err);
      setError(err.message || 'Erreur lors de la réinitialisation du mot de passe. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Vérification...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-3xl text-clinic-blue"></i>
              </div>
              <h2 className="text-3xl font-bold text-clinic-blue-dark">Nouveau mot de passe</h2>
              <p className="text-gray-600 mt-2">
                Pour des raisons de sécurité, vous devez définir un nouveau mot de passe.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-clinic-green/10 border-2 border-clinic-green/30 text-clinic-green rounded-2xl text-sm">
                Mot de passe réinitialisé avec succès ! Redirection vers votre espace...
              </div>
            )}

            {!success && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder="Minimum 10 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-clinic-blue-dark transition-colors cursor-pointer"
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Confirmer le nouveau mot de passe
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="Retapez votre mot de passe"
                  />
                </div>

                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl">
                  <p className="text-sm text-amber-800">
                    <i className="ri-shield-check-line mr-2"></i>
                    Choisissez un mot de passe fort que vous seul connaissez. Il ne doit pas avoir été compromis dans une fuite de données.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}